<template>
  <div
    data-component="layout-switcher-workspace"
    :class="{ 'is-modal-open': isModalOpen }"
  >
    <div class="header-top-left">
      {{ $t("HITL.agent-ui.layout-picker.active-layout") }}
    </div>

    <div class="header-top-right">
      <div class="header-top-label">
        {{ $t("HITL.agent-ui.layout-picker.other-layouts") }}
      </div>

      <or-button
        :variant="ButtonVariant.Text"
        data-button="layout-switcher-create-layout"
        @click="toggleCreateModalOpen(true)"
      >
        <or-icon icon="add" />
        {{ $t("HITL.agent-ui.layout-picker.create-new-layout") }}
      </or-button>
    </div>

    <div class="content-left">
      <layout-switcher-card
        :layout="defaultLayout"
        :default-layout="getDefaultLayout(defaultLayout.key)"
        :show-warning="showWarning(defaultLayout.key)"
        :is-active="true"
        @bookmark="$emit('layout-bookmark', { key: defaultLayout.key })"
        @reset="$emit('layout-reset', { key: defaultLayout.key })"
      />
    </div>

    <div
      v-if="isListEmpty"
      class="content-right-empty"
    >
      <img
        :src="emptyContentImageSrc"
        class="empty-layouts-image"
      >

      <div class="empty-layouts-title">
        {{ $t("HITL.agent-ui.layout-picker.empty-layouts-title") }}
      </div>

      <or-label class="empty-layouts-description">
        {{ $t('HITL.agent-ui.layout-picker.empty-layouts-description') }}
      </or-label>
    </div>

    <div
      v-if="!isListEmpty"
      class="content-right"
    >
      <layout-switcher-card
        v-for="item in listItems"
        :key="item.key"
        :layout="item"
        :default-layout="getDefaultLayout(item.key)"
        :show-warning="showWarning(item.key)"
        @select="$emit('layout-select', { key: item.key })"
        @bookmark="$emit('layout-bookmark', { key: item.key })"
        @reset="$emit('layout-reset', { key: item.key })"
        @delete="$emit('layout-delete', { key: item.key })"
      />
    </div>

    <base-create-layout-popup
      v-if="isCreateModalOpen"
      :is-open="isCreateModalOpen"
      :components="components"
      :is-agent-app="true"
      @cancel="toggleCreateModalOpen(false)"
      @create="onCreateLayout"
    />
  </div>
</template>

<script lang="ts">
import _ from 'lodash';
import {
  defineComponent,
  PropType,
  toRefs,
  ref,
  computed,
  getCurrentInstance,
} from 'vue-demi';
import {
  OrButtonV3 as OrButton,
  OrIconV3 as OrIcon,
  ButtonVariant,
} from '@onereach/ui-components-25';

import LayoutSwitcherCard from './LayoutSwitcherCard.vue';
import { BaseCreateLayoutPopup } from '@/modules/common-layout-builder';
import { Layout, LayoutComponent, LayoutWorkspace } from '@/types';

export default defineComponent({
  name: 'LayoutSwitcherWorkspace',
  components: {
    OrButton,
    OrIcon,
    BaseCreateLayoutPopup,
    LayoutSwitcherCard,
  },
  props: {
    components: {
      type: Object as PropType<Record<string, LayoutComponent>>,
      default: () => new Object(),
    },
    workspace: {
      type: Object as PropType<LayoutWorkspace>,
      default: () => new Object(),
    },
    defaultWorkspace: {
      type: Object as PropType<LayoutWorkspace>,
      default: () => new Object(),
    },
  },
  emits: [
    'layout-select',
    'layout-bookmark',
    'layout-reset',
    'layout-delete',
    'layout-create',
  ],
  setup(props) {
    const { components, workspace, defaultWorkspace } = toRefs(props);
    const vm = getCurrentInstance()!;

    const isDeleteModalOpen = ref(false);
    const isCreateModalOpen = ref(false);

    const isModalOpen = computed(() => {
      return isDeleteModalOpen.value || isCreateModalOpen.value;
    });

    const defaultLayout = computed(() => {
      return workspace.value.layouts[workspace.value.defaultLayout];
    });

    const listItems = computed(() => {
      const { layouts, defaultLayout } = workspace.value;
      const otherLayouts = _.omit(layouts, defaultLayout) as Record<string, Layout>;

      return _.orderBy(
        otherLayouts,
        [
          layout => new Date(layout.bookmarkedAt ?? 0),
          layout => new Date(layout.createdAt),
        ],
        ['desc', 'desc'],
      );
    });

    const isListEmpty = computed(() => listItems.value.length === 0);
    const emptyContentImageSrc = computed(() => {
      const module = require('../assets/directory-open.svg');
      return module.default ?? module;
    });

    const showWarning = (layoutKey: string) => {
      const layout = workspace.value.layouts[layoutKey];
      return !!Object.keys(layout.items).find(key => {
        return !workspace.value.components.includes(key) || !components.value[key];
      });
    };

    const getDefaultLayout = (key: string) => defaultWorkspace.value.layouts[key];
    const toggleCreateModalOpen = (isOpen: boolean) => isCreateModalOpen.value = isOpen;

    const onCreateLayout = ($event: Record<string, any>) => {
      toggleCreateModalOpen(false);
      vm.proxy.$emit('layout-create', $event);
    };

    return {
      ButtonVariant,
      isModalOpen,
      isCreateModalOpen,
      defaultLayout,
      listItems,
      isListEmpty,
      emptyContentImageSrc,
      showWarning,
      getDefaultLayout,
      toggleCreateModalOpen,
      onCreateLayout,
    };
  },
});
</script>

<style lang="scss" scoped>
[data-component='layout-switcher-workspace'] {
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-rows: auto 1fr;
  grid-column-gap: var(--s-6);
  grid-row-gap: var(--s-4);
  padding: var(--s-6);
  color: var(--or-c-on-background);
  background: var(--or-c-surface-1);

  .header-top-left,
  .header-top-right {
    font-size: var(--fs-0);
    color: var(--or-c-on-surface);
  }

  .header-top-left {
    display: flex;
    align-items: center;
  }

  .header-top-right {
    display: flex;
    align-items: center;
    gap: var(--s-4);

    .or-button-v3 {
      padding: var(--s-2);
      font-size: inherit;
      margin-left: auto;
    }
  }

  .content-right-empty {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    background: var(--or-c-surface-2);

    .empty-layouts-title {
      margin-top: var(--s-4);
      font-weight: var(--fw-2);
      color: var(--or-c-on-background);
    }

    .empty-layouts-description {
      margin-top: var(--s-2);
      font-size: var(--fs-0);
      line-height: 1.5;
      color: var(--or-c-outline);
    }
  }

  .content-right {
    display: flex;
    gap: var(--s-4);
    overflow-x: scroll;

    [data-component='base-layout-card'] {
      flex: 0 0 auto;
    }
  }
}
</style>
